import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";
import { Menu, X, ChevronDown, ArrowRight, Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { EASE_OUT } from "../../lib/motion";
import { posthog } from "../../lib/posthog";
import { PromoOffer } from "../promo/PromoOffer";
import { BookDemo } from "../demo/BookDemo";

const IVIZ_APP_URL = "https://iviz.coharts.com/login";
const IVIZ_ICON = "/brand/iviz/iviz-icon-transparent.svg";
const FORESIGHT_MARK = "/brand/foresight/foresight-mark.svg";
const COHARTS_LOGO = "/brand/coharts/coharts-logo-full.png";
const SPIN_SLOW = "animate-[spin_7s_linear_infinite]";

function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return <span className="w-10 h-10 shrink-0" />;
  const isDark = resolvedTheme === "dark";
  return (
    <button
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full text-[#0A0A0F]/70 dark:text-white/70 hover:text-[#0A0A0F] dark:hover:text-white transition-colors"
    >
      {isDark ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
}

export function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const productsRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const reduced = useReducedMotion();
  const menuTransition = { duration: reduced ? 0.1 : 0.22, ease: EASE_OUT };

  useEffect(() => {
    setMobileOpen(false);
    setProductsOpen(false);
    setContactOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        setProductsOpen(false);
        setContactOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (productsRef.current && !productsRef.current.contains(e.target as Node)) setProductsOpen(false);
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) setContactOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function closeMenus() {
    setMobileOpen(false);
    setProductsOpen(false);
    setContactOpen(false);
  }

  const linkCls = (path: string) =>
    `text-sm font-medium transition-colors ${location.pathname === path ? "text-[#1E4FD8] dark:text-[#8CC0F0]" : "text-[#0A0A0F]/70 dark:text-white/60 hover:text-[#0A0A0F] dark:hover:text-white"}`;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FAFAFA] dark:bg-[#050505] border-b border-[#0A0A0F]/8 dark:border-white/8">
      {location.pathname === "/" && <PromoOffer variant="banner" location="top_promo_banner" />}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[80px] flex items-center justify-between gap-8">
        <Link to="/" onClick={closeMenus} className="shrink-0 flex items-center">
          <img src={COHARTS_LOGO} alt="Coharts" className="h-11 w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-7">
          <Link to="/" onClick={closeMenus} className={linkCls("/")}>Home</Link>
          <div ref={productsRef} className="relative">
            <button
              onClick={() => { setProductsOpen((v) => !v); setContactOpen(false); }}
              aria-expanded={productsOpen}
              aria-haspopup="true"
              className="flex items-center gap-1 text-sm font-medium text-[#0A0A0F]/70 dark:text-white/60 hover:text-[#0A0A0F] dark:hover:text-white transition-colors"
            >
              Products <ChevronDown size={14} className={`transition-transform ${productsOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
            {productsOpen && (
              <motion.div
                initial={{ opacity: 0, y: reduced ? 0 : -8, scale: reduced ? 1 : 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: reduced ? 0 : -4, transition: { duration: 0.12 } }}
                transition={menuTransition}
                style={{ transformOrigin: "top left" }}
                className="absolute top-full left-0 mt-3 w-72 rounded-2xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] shadow-xl shadow-black/5 dark:shadow-black/40 overflow-hidden">
                <Link
                  to="/iviz"
                  onClick={closeMenus}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#0A0A0F]/[0.03] dark:hover:bg-white/5 transition-colors"
                >
                  <img src={IVIZ_ICON} alt="" className={`w-8 h-8 shrink-0 ${SPIN_SLOW}`} />
                  <span>
                    <span className="block text-sm font-medium text-[#0A0A0F] dark:text-white">iViz</span>
                    <span className="block text-xs text-[#5C5C66] dark:text-[#9A9A9A]">AI Analytical Workbench</span>
                  </span>
                </Link>
                <Link
                  to="/foresight"
                  onClick={closeMenus}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#0A0A0F]/[0.03] dark:hover:bg-white/5 transition-colors border-t border-[#0A0A0F]/8 dark:border-white/8"
                >
                  <img src={FORESIGHT_MARK} alt="" className={`w-8 h-8 shrink-0 ${SPIN_SLOW}`} />
                  <span>
                    <span className="block text-sm font-medium text-[#0A0A0F] dark:text-white">Foresight</span>
                    <span className="block text-xs text-[#5C5C66] dark:text-[#9A9A9A]">Clinical Trial Simulation</span>
                  </span>
                </Link>
              </motion.div>
            )}
            </AnimatePresence>
          </div>
          <Link to="/pricing" onClick={closeMenus} className={linkCls("/pricing")}>Pricing</Link>
          <Link to="/evidence" onClick={closeMenus} className={linkCls("/evidence")}>Evidence</Link>
          <div ref={contactRef} className="relative">
            <button
              onClick={() => { setContactOpen((v) => !v); setProductsOpen(false); }}
              aria-expanded={contactOpen}
              aria-haspopup="true"
              className="flex items-center gap-1 text-sm font-medium text-[#0A0A0F]/70 dark:text-white/60 hover:text-[#0A0A0F] dark:hover:text-white transition-colors"
            >
              Contact <ChevronDown size={14} className={`transition-transform ${contactOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
            {contactOpen && (
              <motion.div
                initial={{ opacity: 0, y: reduced ? 0 : -8, scale: reduced ? 1 : 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: reduced ? 0 : -4, transition: { duration: 0.12 } }}
                transition={menuTransition}
                style={{ transformOrigin: "top right" }}
                className="absolute top-full right-0 mt-3 w-64 rounded-2xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] shadow-xl shadow-black/5 dark:shadow-black/40 overflow-hidden"
              >
                <Link to="/contact/iviz" onClick={closeMenus} className="block px-4 py-3.5 hover:bg-[#0A0A0F]/[0.03] dark:hover:bg-white/5 transition-colors">
                  <span className="block text-sm font-medium text-[#0A0A0F] dark:text-white">Contact iViz</span>
                  <span className="block text-xs text-[#5C5C66] dark:text-[#9A9A9A]">Data analysis and teams</span>
                </Link>
                <Link to="/contact/foresight" onClick={closeMenus} className="block border-t border-[#0A0A0F]/8 dark:border-white/8 px-4 py-3.5 hover:bg-[#0A0A0F]/[0.03] dark:hover:bg-white/5 transition-colors">
                  <span className="block text-sm font-medium text-[#0A0A0F] dark:text-white">Contact Foresight</span>
                  <span className="block text-xs text-[#5C5C66] dark:text-[#9A9A9A]">Clinical trial simulation</span>
                </Link>
              </motion.div>
            )}
            </AnimatePresence>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          <ThemeToggle />
          <BookDemo location="navbar" variant="nav" product={location.pathname === "/iviz" || location.pathname === "/contact/iviz" ? "iviz" : "foresight"} />
          {location.pathname !== "/iviz" && (
            <a
              href={IVIZ_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => posthog.capture("cta_clicked", { cta: "Launch iViz", location: "navbar" })}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium rounded-full text-white dark:text-[#050505] bg-[#1E4FD8] dark:bg-[#4A90D9] transition-transform hover:scale-[1.02] active:scale-[0.98] active:brightness-90"
            >
              Launch iViz <ArrowRight size={14} />
            </a>
          )}
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            className="w-10 h-10 flex items-center justify-center text-[#0A0A0F] dark:text-white"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
      {mobileOpen && (
        <motion.div
          id="mobile-navigation"
          initial={{ opacity: 0, height: reduced ? "auto" : 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: reduced ? "auto" : 0 }}
          transition={menuTransition}
          className="md:hidden overflow-hidden">
        <div className="bg-[#FAFAFA]/98 dark:bg-[#050505]/98 backdrop-blur-xl border-t border-[#0A0A0F]/8 dark:border-white/8 px-6 py-5 flex flex-col gap-4">
          <Link to="/" onClick={closeMenus} className="text-left text-sm font-medium text-[#0A0A0F]/70 dark:text-white/70">Home</Link>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C66] dark:text-[#9A9A9A]">Products</p>
          <Link to="/iviz" onClick={closeMenus} className="text-left text-sm font-medium text-[#0A0A0F]/70 dark:text-white/70">iViz · AI Analytical Workbench</Link>
          <Link to="/foresight" onClick={closeMenus} className="text-left text-sm font-medium text-[#0A0A0F]/70 dark:text-white/70">Foresight · Clinical Trial Simulation</Link>
          <Link to="/pricing" onClick={closeMenus} className="text-left text-sm font-medium text-[#0A0A0F]/70 dark:text-white/70">Pricing</Link>
          <Link to="/evidence" onClick={closeMenus} className="text-left text-sm font-medium text-[#0A0A0F]/70 dark:text-white/70">Evidence</Link>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C66] dark:text-[#9A9A9A]">Contact</p>
          <Link to="/contact/iviz" onClick={closeMenus} className="text-left text-sm font-medium text-[#0A0A0F]/70 dark:text-white/70">Contact iViz</Link>
          <Link to="/contact/foresight" onClick={closeMenus} className="text-left text-sm font-medium text-[#0A0A0F]/70 dark:text-white/70">Contact Foresight</Link>
          <BookDemo location="navbar_mobile" variant="nav" product={location.pathname === "/iviz" || location.pathname === "/contact/iviz" ? "iviz" : "foresight"} className="justify-center" />
          {location.pathname !== "/iviz" && (
            <a
              href={IVIZ_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => posthog.capture("cta_clicked", { cta: "Launch iViz", location: "navbar_mobile" })}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 text-sm font-medium rounded-full text-white dark:text-[#050505] bg-[#1E4FD8] dark:bg-[#4A90D9]"
            >
              Launch iViz <ArrowRight size={14} />
            </a>
          )}
        </div>
        </motion.div>
      )}
      </AnimatePresence>
    </nav>
  );
}
